<script setup lang="ts">
import Slider from '@/widgets/slider/index';
import Badge from '@/shared/ui/Badge/index.vue';
import { useModalsStore } from '@/app/store/modals';
import type { IBadgeItems } from '@/shared/ui/Badge/types';
import type { IImagesSlider } from '@/pages/product-detail/model/types/imagesSlider';
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';
import { MIN_BOUGHT_AMOUNT } from '@/shared/constants/badges';

const props = withDefaults(defineProps<IImagesSlider>(), {
  isShowNavigation: false,
  isShowPagination: false,
  loop: true,
  gap: 20,
  itemsToShow: 1,
  imageWidth: '100%',
  imageHeight: '500px',
  isShowBadges: false,
  isShowModal: false,
  startIndex: 0,
});

const { setModalState } = useModalsStore();
const isMobile = inject('isMobile');
const { t } = useI18n();

const emit = defineEmits<{
  openModal: [index: number];
}>();

const images = computed(() => props.product.images || []);
const isShowSlider = computed(() => images.value.length > 1);

const badges: Ref<IBadgeItems[]> = ref([
  {
    id: 1,
    text: 'Ликвидация',
    type: 'liquidation',
    isShow: Boolean(props.product.liquidation),
  },
  {
    id: 2,
    text: String(props.product.bought_count),
    type: 'bought',
    isShow: Boolean(props.product.bought_count > MIN_BOUGHT_AMOUNT),
  },
  {
    id: 3,
    text: String(props.product.in_stock),
    type: 'remained',
    isShow: Boolean(props.product.in_stock),
  },
  {
    id: 4,
    text: 'Новинка',
    type: 'new_arrival',
    isShow: Boolean(props.product.new_arrival),
  },
]);

const openModal = (index: number) => {
  emit('openModal', index);
  setModalState('product', true);
};
</script>

<template>
  <div :class="['product-detail-images-slider', { '_one-slide': !isShowSlider }]">
    <template v-if="isShowSlider">
      <Slider
        :slides="images"
        :items-to-show="isMobile && isShowModal ? 1.15 : 1"
        effect="slide"
        align="start"
        :is-show-navigation="isShowNavigation && images.length > 1"
        :is-show-pagination="isShowPagination && images.length > 1"
        :gap="gap"
        :loop="loop"
        :start-index="startIndex"
        class="product-detail-images-slider__slider"
      >
        <template #slide="{ slide, index }">
          <div class="product-detail-images-slider__slide">
            <button
              class="product-detail-images-slider__button"
              :aria-label="t('viewProductPhoto')"
              @click.prevent="isShowModal ? openModal(index) : {}"
            >
              <NuxtImg
                :src="slide?.image"
                loading="lazy"
                :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
                alt="Фото товара"
                class="product-detail-images-slider__image"
              />
            </button>
            <ul v-if="isShowBadges" class="product-detail-images-slider__badges">
              <li
                v-for="badge in badges"
                :key="badge.id"
                v-show="badge.isShow"
                class="product-detail-images-slider__badge"
              >
                <Badge :id="badge.id" :text="badge?.text" :type="badge?.type" />
              </li>
            </ul>
          </div>
        </template>
      </Slider>
    </template>
    <template v-else>
      <div class="product-detail-images-slider__slide">
        <button
          class="product-detail-images-slider__button"
          :aria-label="t('viewProductPhoto')"
          @click.prevent="isShowModal ? openModal(0) : {}"
        >
          <NuxtImg
            :src="images[0]?.image"
            loading="lazy"
            :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
            alt="Фото товара"
            class="product-detail-images-slider__image"
          />
        </button>
        <ul v-if="isShowBadges" class="product-detail-images-slider__badges">
          <li
            v-for="badge in badges"
            :key="badge.id"
            v-show="badge.isShow"
            class="product-detail-images-slider__badge"
          >
            <Badge :id="badge.id" :text="badge?.text" :type="badge?.type" />
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<style lang="less">
.product-detail-images-slider {
  display: flex;
  justify-content: center;

  &._one-slide {
    align-items: center;
  }

  &__slider {
    width: 100%;

    .embla__button {
      &._prev {
        left: -44px;
      }

      &._next {
        right: -44px;
      }
    }
  }

  &__slide {
    position: relative;
  }

  &__image {
    display: block;
    object-fit: cover;
    width: v-bind(imageWidth);
    height: v-bind(imageHeight);
    border-radius: @size-x4;
  }

  &__badges {
    .flex-column(@main-size);

    position: absolute;
    left: @size-x3;
    bottom: @size-x3;
  }

  &__button {
    .button-reset();

    display: flex;
    width: v-bind(imageWidth);
    height: v-bind(imageHeight);
    cursor: default;
  }

  @media @1024 {
    &__slider {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      .embla__button {
        &._prev {
          left: @size-x3;
        }

        &._next {
          right: @size-x3;
        }
      }

      .embla__dots {
        bottom: -@size-x4;
      }

      .embla__dot {
        background: @color-silver-chalice;
        opacity: 0.5;

        &._selected {
          width: @size-x2;
          height: @size-x2;
          background: @color-cod-gray;
          opacity: 0.5;
        }
      }
    }

    &__slide {
      width: 100%;
    }

    &__image {
      border-radius: @size-x3;
    }
  }
}
</style>
